/**
 * Order Acknowledgements Import Modal (CR-6679)
 *
 * 3-step wizard for bulk-acknowledging PO lines via Excel/CSV upload:
 * Step 1 (Upload): Download template + select file
 * Step 2 (Preview): Validation results with discrepancy highlighting
 * Step 3 (Result): Summary of applied acknowledgements
 */
import { useState } from 'react';
import { X, Upload, Loader2, AlertCircle, CheckCircle, FileSpreadsheet, Download, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { purchasingAPI } from '../../lib/api';
import type { AckImportPreviewRow, AckImportPreviewResponse, AckImportConfirmResponse } from '../../lib/api/types/po-types';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/errorUtils';
import FileDropZone from '../common/FileDropZone';
import OrderAckPreviewTable from './OrderAckPreviewTable';

interface OrderAckImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
  /** Total count of lines matching current filters — shown in the instructions */
  totalCount?: number;
  /** Current view/filter params — template will match exactly what's in the table */
  exportParams?: Record<string, string>;
}

type ImportStep = 'upload' | 'preview' | 'result';

export default function OrderAckImportModal({
  isOpen,
  onClose,
  onImportComplete,
  totalCount,
  exportParams = {},
}: OrderAckImportModalProps) {
  const [step, setStep] = useState<ImportStep>('upload');
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingPreview, setDownloadingPreview] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<AckImportPreviewRow[]>([]);
  const [previewSummary, setPreviewSummary] = useState<{
    total_rows: number;
    valid_count: number;
    discrepancy_count: number;
    error_count: number;
  } | null>(null);
  const [previewErrors, setPreviewErrors] = useState<Array<{ row: number; message: string }>>([]);
  const [importResult, setImportResult] = useState<AckImportConfirmResponse | null>(null);

  // ── Download Template ───────────────────────────────────────────────────────
  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const response = await purchasingAPI.ackImportTemplate(exportParams);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `order_acknowledgement_template_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Template downloaded');
    } catch {
      toast.error('Failed to download template');
    } finally {
      setDownloading(false);
    }
  };

  // Customer review 11 Sep (drag and drop everywhere): FileDropZone enforces
  // .xlsx/.xls/.csv for drops and picks, naming any refused file.
  const handleFileSelect = (files: File[]) => {
    if (files[0]) setFile(files[0]);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await purchasingAPI.ackImportPreview(formData);
      const data = response.data as AckImportPreviewResponse;

      if (data.status === 'success' || data.status === 'error') {
        setPreviewData(data.preview || []);
        setPreviewSummary({
          total_rows: data.total_rows,
          valid_count: data.valid_count,
          discrepancy_count: data.discrepancy_count,
          error_count: data.error_count,
        });
        setPreviewErrors(data.errors || []);
        setBatchId(data.batch_id || null);
        setStep('preview');
      }
    } catch (err: unknown) {
      console.error('Import preview failed:', err);
      toast.error(getApiErrorMessage(err, 'Failed to process file'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    // Build rows for confirm — include valid and discrepancy rows (discrepancy rows proceed with comment)
    const rowsToConfirm = previewData
      .filter(row => row.status === 'valid' || row.status === 'discrepancy')
      .map(row => ({
        row_number: row.row,
        po_number: row.po_number,
        line_number: row.line_number,
        ack_date: row.ack_date || undefined,
        ack_qty: row.ack_qty ? parseFloat(row.ack_qty) : undefined,
        ack_unit_price: row.ack_unit_price ? parseFloat(row.ack_unit_price) : undefined,
      }));

    if (rowsToConfirm.length === 0) {
      toast.error('No valid rows to import');
      return;
    }

    setLoading(true);
    try {
      const response = await purchasingAPI.ackImportConfirm({
        batch_id: batchId || undefined,
        rows: rowsToConfirm,
      });
      const data = response.data as AckImportConfirmResponse;

      setImportResult(data);
      setStep('result');

      if (data.acknowledged > 0) {
        toast.success(`${data.acknowledged} line${data.acknowledged !== 1 ? 's' : ''} acknowledged`);
      }
    } catch (err: unknown) {
      console.error('Import confirmation failed:', err);
      toast.error(getApiErrorMessage(err, 'Failed to import acknowledgements'));
    } finally {
      setLoading(false);
    }
  };

  // ── Download Preview Results ────────────────────────────────────────────────
  const handleDownloadPreview = async () => {
    if (previewData.length === 0) {
      toast.error('No preview data to download');
      return;
    }

    setDownloadingPreview(true);
    try {
      const response = await purchasingAPI.ackImportDownloadPreview(previewData);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ack-import-preview-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Preview results downloaded');
    } catch {
      toast.error('Failed to download preview results');
    } finally {
      setDownloadingPreview(false);
    }
  };

  const handleClose = () => {
    if (step === 'result' && importResult && importResult.acknowledged > 0) {
      onImportComplete();
    }
    resetState();
    onClose();
  };

  const resetState = () => {
    setStep('upload');
    setFile(null);
    setBatchId(null);
    setPreviewData([]);
    setPreviewSummary(null);
    setPreviewErrors([]);
    setImportResult(null);
  };

  const resetToUpload = () => {
    // If we have a batch_id for a preview, cancel it
    if (batchId) {
      purchasingAPI.ackImportCancel(batchId).catch(() => {
        // Silently ignore cancel errors
      });
    }
    setStep('upload');
    setFile(null);
    setBatchId(null);
    setPreviewData([]);
    setPreviewSummary(null);
    setPreviewErrors([]);
  };

  if (!isOpen) return null;

  const validRowCount = previewData.filter(row => row.status === 'valid').length;
  const discrepancyRowCount = previewData.filter(row => row.status === 'discrepancy').length;
  const errorRowCount = previewData.filter(row => row.status === 'error').length;
  const importableCount = validRowCount + discrepancyRowCount;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">
              {step === 'upload' && 'Import Order Acknowledgements'}
              {step === 'preview' && 'Preview Import Data'}
              {step === 'result' && 'Import Complete'}
            </h2>
          </div>
          <button onClick={handleClose} className="p-1 hover:bg-muted rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div className="space-y-6">
              {/* Instructions */}
              <div className="rounded-md bg-muted/50 border border-border px-4 py-3 text-sm space-y-1">
                <p><span className="font-medium">Step 1:</span> Download the template — it will contain {totalCount ? `${totalCount.toLocaleString()} ` : ''}PO lines matching your current view and filters.</p>
                <p><span className="font-medium">Step 2:</span> Edit the yellow-highlighted columns: <span className="font-semibold">Ack Qty</span>, <span className="font-semibold">Ack Unit Price</span>, <span className="font-semibold">Ack Date</span>, and <span className="font-semibold">Comment</span>.</p>
                <p><span className="font-medium">Step 3:</span> Upload the file — data will be validated and any discrepancies flagged for review.</p>
                <p className="text-muted-foreground mt-1">Only lines with status <span className="font-semibold">New</span> or <span className="font-semibold">Changed</span> can be acknowledged. Lines with other statuses will be skipped.</p>
              </div>

              {/* Download Template Button */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleDownloadTemplate}
                  disabled={downloading}
                  className="flex items-center gap-2"
                >
                  {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  Download Template
                </Button>
              </div>

              {/* File Drop Zone */}
              <FileDropZone
                accept=".xlsx,.xls,.csv"
                hint="Supported formats: .xlsx, .xls, .csv"
                onFiles={handleFileSelect}
                className="p-8"
                data-testid="order-ack-import-drop-zone"
              >
                {file && (
                  <p className="text-sm">
                    <span className="font-medium text-primary">{file.name}</span>
                    <span className="text-muted-foreground"> · {(file.size / 1024).toFixed(1)} KB</span>
                  </p>
                )}
              </FileDropZone>

              {/* Editable Columns Info */}
              <div className="bg-muted/30 rounded-lg p-4">
                <h3 className="font-medium mb-2">Editable Columns (highlighted in yellow)</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-muted-foreground">
                  <span>Ack Qty</span>
                  <span>Ack Unit Price</span>
                  <span>Ack Date</span>
                  <span>Comment</span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  If your values differ from the PO data (Part Number, Drawing Revision, Unit Price, Required Date),
                  discrepancies will be flagged for your review before import.
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 'preview' && previewSummary && (
            <div className="space-y-4">
              {/* Summary + Download Button */}
              <div className="flex items-center justify-between gap-4 p-4 bg-muted/30 rounded-lg flex-wrap">
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Total Rows:</span>
                    <span className="font-semibold">{previewSummary.total_rows}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Valid: {validRowCount}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    <span className="text-sm">Discrepancies: {discrepancyRowCount}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-red-500" />
                    <span className="text-sm">Errors: {errorRowCount}</span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadPreview}
                  disabled={downloadingPreview}
                  className="flex items-center gap-2"
                >
                  {downloadingPreview ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  Download Preview
                </Button>
              </div>

              {/* Discrepancy Warning */}
              {discrepancyRowCount > 0 && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="font-medium text-amber-800 mb-2">Discrepancies Detected</h4>
                  <p className="text-sm text-amber-700">
                    {discrepancyRowCount} row{discrepancyRowCount > 1 ? 's have' : ' has'} values that differ from the PO data.
                    These rows will still be imported — discrepancies are logged for reference.
                  </p>
                </div>
              )}

              {/* Errors Summary */}
              {previewErrors.length > 0 && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="font-medium text-red-800 mb-2">Validation Errors</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {previewErrors.slice(0, 10).map((err, idx) => (
                      <div key={idx} className="text-sm text-red-700">
                        <span className="font-medium">Row {err.row}:</span> {err.message}
                      </div>
                    ))}
                    {previewErrors.length > 10 && (
                      <p className="text-sm text-red-600 mt-2">
                        And {previewErrors.length - 10} more errors...
                      </p>
                    )}
                  </div>
                </div>
              )}

              <OrderAckPreviewTable previewData={previewData} />
            </div>
          )}

          {/* Step 3: Result */}
          {step === 'result' && importResult && (
            <div className="space-y-6 text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
              <h3 className="text-xl font-semibold">Import Completed!</h3>

              <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{importResult.acknowledged}</p>
                  <p className="text-sm text-muted-foreground">Lines Acknowledged</p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-2xl font-bold">{importResult.total}</p>
                  <p className="text-sm text-muted-foreground">Total Processed</p>
                </div>
              </div>

              {importResult.errors > 0 && importResult.error_details && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg max-w-lg mx-auto">
                  <h4 className="font-medium text-red-800 mb-2">
                    {importResult.errors} row{importResult.errors !== 1 ? 's' : ''} failed:
                  </h4>
                  <div className="space-y-1 text-left max-h-40 overflow-y-auto">
                    {importResult.error_details.map((err, idx) => (
                      <p key={idx} className="text-sm text-red-700">
                        <span className="font-medium">Row {err.row}</span>
                        {err.po_number && ` (PO ${err.po_number})`}: {err.message}
                      </p>
                    ))}
                    {importResult.errors_truncated > 0 && (
                      <p className="text-sm text-red-600 mt-2">
                        And {importResult.errors_truncated} more errors...
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-4 border-t">
          {step === 'preview' && (
            <Button variant="outline" onClick={resetToUpload} disabled={loading}>
              Back
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            {step === 'result' ? 'Done' : 'Cancel'}
          </Button>

          {step === 'upload' && (
            <Button onClick={handleUpload} disabled={!file || loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Preview Import
                </>
              )}
            </Button>
          )}

          {step === 'preview' && (
            <Button onClick={handleConfirmImport} disabled={importableCount === 0 || loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Acknowledge {importableCount} Line{importableCount !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
